
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import TicketStatusChart from './dashboard/TicketStatusChart';
import RechnungenChart from './dashboard/RechnungenChart';
import LeerstandWidget from './dashboard/LeerstandWidget';
import TermineWocheWidget from './dashboard/TermineWocheWidget';
import MeinePruefungenWidget from './dashboard/MeinePruefungenWidget';
import BriefingKopf from './dashboard/BriefingKopf';
import EmailAssistentWidget from './dashboard/EmailAssistentWidget';
import ArbeitsvorschlagWidget from './dashboard/ArbeitsvorschlagWidget';
import { ladeBriefingMails, type BriefingMail } from '../lib/dashboard/briefing';
import { ladeSichtbareRechnungen, type SichtRechnung } from '../lib/dashboard/rechnungenSicht';
import { istMeinBeleg, nurEigeneSicht, type DashboardScope, type ScopeNutzer } from '../lib/dashboard/scope';
import { DashboardWidget, KopfKachel, KpiKarte, WidgetKopf, WidgetLeer, type WidgetFarbe } from './dashboard/DashboardWidget';
import { ArrowRight, ArrowsClockwise, Buildings, CalendarBlank, CalendarCheck, CheckCircle, CircleNotch, ClockCounterClockwise, FileText, Files as FilesFill, CurrencyEur, Receipt as ReceiptFill, Sparkle, Ticket, User, Users as UsersFill, UsersThree, WarningCircle, type Icon } from '@phosphor-icons/react';
import { useViewport } from './island/hooks';
import { GlasTooltipSchicht } from './ui/GlasTooltip';
import { begruessung, rasterStufe, spalten } from '../lib/dashboard/layout';
import { terminTypLabel } from '../lib/termine/typLabel';

const span = (n: number) => `span ${n}`;

/** Symbol und Farbe je Aktivitätstyp (Prototyp: Rechnung orange, Dokument gold, Mieter pink, Objekt blau). */
const AKTIVITAET: Record<string, { icon: Icon; farbe: WidgetFarbe }> = {
  rechnung: { icon: ReceiptFill, farbe: '--dw-orange' },
  dokument: { icon: FilesFill, farbe: '--dw-gold' },
  mieter: { icon: UsersFill, farbe: '--dw-pink' },
  objekt: { icon: Buildings, farbe: '--dw-blau' },
};
const AKTIVITAET_SONST: { icon: Icon; farbe: WidgetFarbe } = { icon: FileText, farbe: '--dw-graublau' };

interface DashboardViewProps {
  userProfile?: any;
  onNavigate?: (path: string) => void;
  onNavigateToRechnung?: (id: string) => void;
  onNavigateToTicket?: (id: string) => void;
  onNavigateToEmail?: (id: string) => void;
}

interface Stats {
  rechnungenOffen: number;
  offeneSumme: number;
  ueberfaellig: number;
  kiPool: number;
}

interface OffeneRechnung {
  id: string;
  rechnungsnummer: string;
  unternehmen: string;
  brutto: number;
  faelligkeitsdatum: string | null;
  status: string;
}

interface NaechsterTermin {
  id: string;
  titel: string;
  start_datum: string;
  typ: string;
  typ_freitext?: string | null;
  status: string;
}

interface LetzteAktivitaet {
  id: string;
  typ: 'rechnung' | 'dokument' | 'mieter' | 'objekt';
  titel: string;
  details: string;
  zeitpunkt: string;
}

const DashboardView: React.FC<DashboardViewProps> = ({ userProfile, onNavigate, onNavigateToRechnung, onNavigateToTicket, onNavigateToEmail }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    rechnungenOffen: 0, offeneSumme: 0, ueberfaellig: 0, kiPool: 0
  });
  const [offeneRechnungen, setOffeneRechnungen] = useState<OffeneRechnung[]>([]);
  const [naechsteTermine, setNaechsteTermine] = useState<NaechsterTermin[]>([]);
  const [letzteAktivitaeten, setLetzteAktivitaeten] = useState<LetzteAktivitaet[]>([]);
  const [briefingMails, setBriefingMails] = useState<BriefingMail[]>([]);
  const [briefingGeladen, setBriefingGeladen] = useState(false);

  const scopeKey = 'dashboard_scope_' + (userProfile?.id || 'anon');
  const [gewaehlterScope, setScope] = useState<DashboardScope>(() =>
    (localStorage.getItem(scopeKey) === 'alle' ? 'alle' : 'meine'));
  const setzeScope = (s: DashboardScope) => { setScope(s); localStorage.setItem(scopeKey, s); };

  // Mitarbeitende (Rolle 'team') sehen ausschließlich ihre eigenen bzw. Team-Vorgänge:
  // kein Umschalter, Kennzahlen und Diagramme auf ihre Zuständigkeit gerechnet, firmenweite
  // Blöcke (Leerstand, Termine, letzte Aktivitäten) gar nicht erst geladen.
  const nurEigene = useMemo(() => nurEigeneSicht(userProfile), [userProfile?.role]);
  const scope: DashboardScope = nurEigene ? 'meine' : gewaehlterScope;

  // Nach interaktivem Login mountet die View ggf. vor dem Profil — gespeicherte Wahl nachladen.
  useEffect(() => {
    if (!userProfile?.id) return;
    const gespeichert = localStorage.getItem('dashboard_scope_' + userProfile.id);
    setScope(gespeichert === 'alle' ? 'alle' : 'meine');
  }, [userProfile?.id]);

  const nutzer: ScopeNutzer = useMemo(
    () => ({ id: userProfile?.id || '', team_ids: userProfile?.team_ids }),
    [userProfile?.id, userProfile?.team_ids],
  );

  useEffect(() => {
    fetchDashboardData();
  }, [userProfile?.id]);

  useEffect(() => {
    ladeBriefingMails()
      .then(setBriefingMails)
      .catch(() => setBriefingMails([]))
      .finally(() => setBriefingGeladen(true));
  }, []);
  const openEmail = (id: string) => onNavigateToEmail?.(id);

  // Raster aus der echten Breite: Fläche per ResizeObserver, Fenster für die mobile Stufe (< 900 px).
  const flaeche = useRef<HTMLDivElement>(null);
  const [verfuegbar, setVerfuegbar] = useState(1200);
  const { width: fenster } = useViewport();
  useEffect(() => {
    const el = flaeche.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setVerfuegbar(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);
  const stufe = rasterStufe(verfuegbar, fenster);
  const sp = spalten(stufe, nurEigene, verfuegbar);
  const mobil = fenster < 900;
  const schmal = fenster < 640;
  const datumText = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Einmal je Dashboard-Laden: sichtbarkeitsgefilterte Belege (personen-/team-orientiert,
      // dieselbe Regel wie die Rechnungsliste) — von fetchStats + fetchOffeneRechnungen geteilt,
      // damit kein Doppel-Fetch entsteht.
      let sicht: { rechnungen: SichtRechnung[]; unklar: SichtRechnung[] };
      try {
        sicht = await ladeSichtbareRechnungen(userProfile);
      } catch (err) {
        // Loader-Fehler darf das Dashboard nicht dauerhaft im Spinner hängen lassen —
        // mit leerer Sicht-Menge weiterrechnen statt die ganze Funktion abzubrechen.
        console.error('Error loading sichtbare Rechnungen:', err);
        sicht = { rechnungen: [], unklar: [] };
      }
      // Für Mitarbeitende zählen nur Belege, die ihnen bzw. ihrem Team gehören — die
      // Sichtbarkeits-Einstellung (Team → Sichtbarkeit) regelt, was sie überhaupt LESEN
      // dürfen, das Dashboard zeigt davon nur ihre eigenen Aufgaben.
      if (nurEigene) {
        sicht = {
          rechnungen: sicht.rechnungen.filter((r) => istMeinBeleg(r, nutzer)),
          unklar: sicht.unklar.filter((r) => istMeinBeleg(r, nutzer)),
        };
      }
      await Promise.all([
        fetchStats(sicht),
        fetchOffeneRechnungen(sicht),
        // Firmenweite Blöcke: für Mitarbeitende weder anzeigen noch laden.
        nurEigene ? Promise.resolve() : fetchNaechsteTermine(),
        nurEigene ? Promise.resolve() : fetchLetzteAktivitaeten(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (sicht: { rechnungen: SichtRechnung[]; unklar: SichtRechnung[] }) => {
    try {
      const rechnungenData = sicht.rechnungen;
      const offene = rechnungenData.filter(r => (r.status || '').toLowerCase() === 'offen');
      const offeneSumme = offene.reduce((sum, r) => sum + (Number(r.brutto) || 0), 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const ueberfaellig = rechnungenData.filter(r =>
        r.status !== 'bezahlt' && r.status !== 'klaerung' && r.faelligkeitsdatum && new Date(r.faelligkeitsdatum) < today
      ).length;
      // KI-Pool ("Prüfung nötig") — dieselbe Definition wie die Karte in InvoicesView.tsx
      // seit cd68bd6: Status 'unklar' aus BEIDEN Tabellen zählen, nicht die Herkunft
      // (is_unklar_source zählte auch längst bezahlte/geklärte Alt-Belege mit).
      const kiPool = rechnungenData.filter(r => (r.status || '').toLowerCase() === 'unklar').length
        + sicht.unklar.filter(r => !r.status || (r.status || '').toLowerCase() === 'unklar').length;

      setStats({
        rechnungenOffen: offene.length,
        offeneSumme,
        ueberfaellig,
        kiPool
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchOffeneRechnungen = async (sicht: { rechnungen: SichtRechnung[]; unklar: SichtRechnung[] }) => {
    try {
      const offene: OffeneRechnung[] = sicht.rechnungen
        .filter(r => (r.status || '').toLowerCase() === 'offen')
        .sort((a, b) => {
          // Fälligkeitsdatum aufsteigend, nulls zuletzt (wie zuvor .order(..., { nullsFirst: false })).
          if (!a.faelligkeitsdatum && !b.faelligkeitsdatum) return 0;
          if (!a.faelligkeitsdatum) return 1;
          if (!b.faelligkeitsdatum) return -1;
          return a.faelligkeitsdatum.localeCompare(b.faelligkeitsdatum);
        })
        .slice(0, 5)
        .map(r => ({
          id: r.id,
          rechnungsnummer: r.rechnungsnummer || '',
          unternehmen: r.unternehmen || '',
          brutto: Number(r.brutto) || 0,
          faelligkeitsdatum: r.faelligkeitsdatum,
          status: r.status || '',
        }));
      setOffeneRechnungen(offene);
    } catch (err) {
      console.error('Error fetching offene rechnungen:', err);
    }
  };

  const fetchNaechsteTermine = async () => {
    try {
      const { data } = await supabase
        .from('termine')
        .select('id, titel, start_datum, typ, typ_freitext, status')
        .gte('start_datum', new Date().toISOString())
        .lte('start_datum', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
        // Abgesagte Termine nicht als "nächste Termine" anzeigen (wie TermineWocheWidget.tsx u. a.).
        .not('status', 'eq', 'abgesagt')
        .order('start_datum', { ascending: true })
        .limit(5);
      setNaechsteTermine(data || []);
    } catch (err) {
      console.error('Error fetching termine:', err);
    }
  };

  const fetchLetzteAktivitaeten = async () => {
    try {
      const [rechnungen, dokumente, mieterNeu] = await Promise.all([
        // Archivierte Belege nicht als "Aktivität" zeigen (konsistent zu fetchStats/RechnungenChart).
        supabase.from('rechnungen').select('id, rechnungsnummer, unternehmen, created_at').is('archiviert_am', null).order('created_at', { ascending: false }).limit(3),
        supabase.from('dokumente').select('id, datei_name, assigned_name, created_at').eq('is_deleted', false).order('created_at', { ascending: false }).limit(3),
        supabase.from('mieter').select('id, vorname, nachname, created_at').order('created_at', { ascending: false }).limit(2)
      ]);

      const activities: LetzteAktivitaet[] = [
        ...(rechnungen.data || []).map(r => ({
          id: r.id,
          typ: 'rechnung' as const,
          titel: `Rechnung ${r.rechnungsnummer}`,
          details: r.unternehmen,
          zeitpunkt: r.created_at
        })),
        ...(dokumente.data || []).map(d => ({
          id: d.id,
          typ: 'dokument' as const,
          titel: d.datei_name,
          details: d.assigned_name || 'Nicht zugewiesen',
          zeitpunkt: d.created_at
        })),
        ...(mieterNeu.data || []).map(m => ({
          id: m.id,
          typ: 'mieter' as const,
          titel: `${m.vorname} ${m.nachname}`,
          details: 'Neuer Mieter',
          zeitpunkt: m.created_at
        }))
      ].sort((a, b) => new Date(b.zeitpunkt).getTime() - new Date(a.zeitpunkt).getTime()).slice(0, 6);

      setLetzteAktivitaeten(activities);
    } catch (err) {
      console.error('Error fetching activities:', err);
    }
  };

  const isOverdue = (date: string | null) => {
    if (!date) return false;
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    return new Date(date) < heute;
  };

  const formatRelativeTime = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `vor ${minutes} Min.`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `vor ${hours} Std.`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Gestern';
    return `vor ${days} Tagen`;
  };

  if (loading) {
    return (
      <div className="dash-flaeche flex flex-col items-center justify-center" style={{ minHeight: '60vh', gap: 12, color: 'var(--ink3)' }}>
        <CircleNotch size={40} weight="bold" className="animate-spin" color="var(--acc)" />
        <span style={{ fontSize: 14, fontWeight: 600 }}>Dashboard wird geladen...</span>
      </div>
    );
  }

  return (
    <div ref={flaeche} className="dash-flaeche flex flex-col" style={{ gap: 14, paddingBottom: 40 }}>
      {/* Kopf: Datum, Begrüßung, Scope (nur mit Gesamtsicht), Aktualisieren */}
      <div className="flex flex-wrap items-end justify-between" style={{ gap: 12, padding: '6px 4px 2px' }}>
        <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>{datumText}</span>
          <h1 className="m-0" style={{ fontSize: schmal ? 26 : 30, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.1 }}>{begruessung(new Date().getHours(), userProfile?.full_name)}</h1>
        </div>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          {/* Umschalter nur für Rollen mit Gesamtsicht — Mitarbeitende sehen ohnehin nur ihre Aufgaben. */}
          {!nurEigene && (
            <div role="tablist" aria-label="Sicht" className="flex" style={{ gap: 3, padding: 3, borderRadius: 16, background: 'var(--glass)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', boxShadow: 'var(--glass-sh)' }}>
              {(['meine', 'alle'] as DashboardScope[]).map((s) => {
                const an = scope === s;
                const I = s === 'meine' ? User : UsersThree;
                return (
                  <button key={s} type="button" role="tab" aria-selected={an} onClick={() => setzeScope(s)}
                    data-tip={s === 'meine' ? 'Meine Aufgaben – Nur Vorgänge in meiner bzw. Team-Zuständigkeit' : 'Alles – Alle Vorgänge der Verwaltung'}
                    className="flex items-center"
                    style={{ height: mobil ? 44 : 34, padding: '0 14px', gap: 7, border: 'none', borderRadius: 13, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)', boxShadow: an ? 'var(--acc-sh)' : 'none', transition: 'background .25s, color .25s' }}>
                    <I size={15} weight={an ? 'fill' : 'bold'} />{s === 'meine' ? 'Meine Aufgaben' : 'Alles'}
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" onClick={() => void fetchDashboardData()} data-tip="Aktualisieren – Alle Kennzahlen und Listen neu laden"
            className="flex items-center"
            style={{ height: mobil ? 44 : 40, padding: '0 14px', gap: 7, border: 'none', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              background: 'var(--glass)', color: 'var(--ink)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', boxShadow: 'var(--glass-sh)' }}>
            <ArrowsClockwise size={15} weight="bold" />Aktualisieren
          </button>
        </div>
      </div>

      {/* Bento-Raster (README §2): 12 Spalten, dichte Füllung, Widgets einer Reihe gleich hoch */}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gridAutoFlow: 'row dense', gap: 12, marginTop: 30 }}>
        {/* Tages-Briefing (kritische Mails + Termine/Verträge) */}
        {briefingGeladen && <BriefingKopf mails={briefingMails} onOpenEmail={openEmail} onNavigate={onNavigate} scope={scope} nutzer={nutzer} nurEigene={nurEigene} spalte={span(sp.briefing)} />}

        {/* Finanz-Übersicht */}
        <KpiKarte ton="blau" icon={CurrencyEur} label="Offene Rechnungen" spalte={span(sp.kpi)} schmal={stufe === 'schmal'} onClick={() => onNavigate?.('/admin/rechnungen')}
          tip="Offene Rechnungen – Summe aller Belege mit Status offen"
          wert={`${stats.offeneSumme.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`} unter={`${stats.rechnungenOffen} Belege ausstehend`} />
        <KpiKarte ton={stats.ueberfaellig > 0 ? 'rot' : 'gruen'} icon={WarningCircle} label="Überfällig" spalte={span(sp.kpi)} schmal={stufe === 'schmal'} onClick={() => onNavigate?.('/admin/rechnungen')}
          tip="Überfällig – Nicht bezahlte Belege mit abgelaufener Fälligkeit (ohne Klärung)"
          wert={stats.ueberfaellig} unter="Fristen abgelaufen" />
        <KpiKarte ton="lila" icon={Sparkle} label="KI-Pool" spalte={span(sp.kpi)} schmal={stufe === 'schmal'} onClick={() => onNavigate?.('/admin/rechnungen')}
          tip="KI-Pool – Belege mit Status unklar, die geprüft werden müssen"
          wert={stats.kiPool} unter="Prüfung nötig" />

        {/* Arbeitsvorschlag des Tages */}
        {briefingGeladen && (
          <div className="flex flex-col [&>*]:flex-1" style={{ gridColumn: span(sp.arbeit) }}>
            <ArbeitsvorschlagWidget mails={briefingMails} onOpenEmail={openEmail} onNavigateToTicket={onNavigateToTicket} onNavigateToRechnung={onNavigateToRechnung} userProfile={userProfile} scope={scope} nutzer={nutzer} />
          </div>
        )}

        {/* Meine Prüfungen (personalisiert: Rechnungen, die mir zur Prüfung/Freigabe vorliegen) */}
        <div className="flex flex-col [&>*]:flex-1" style={{ gridColumn: span(sp.pruef) }}>
          <MeinePruefungenWidget userProfile={userProfile} onNavigate={onNavigate} onNavigateToRechnung={onNavigateToRechnung} />
        </div>

        {/* Auslastung (firmenweit, nicht für Mitarbeitende) über Schnellzugriff */}
        <div className="flex flex-col" style={{ gridColumn: span(sp.auslastung), gap: 12 }}>
          {!nurEigene && <div className="flex flex-1 flex-col [&>*]:flex-1"><LeerstandWidget onNavigate={onNavigate} /></div>}
          {/* Schnellzugriff */}
          <section aria-label="Schnellzugriff" className="dw dw-still flex flex-col justify-center"
            style={{ flex: nurEigene ? '1 0 auto' : '0 0 auto', borderRadius: 26, padding: '14px 16px 22px', gap: 10, background: 'var(--glass)',
              backdropFilter: 'blur(28px) saturate(170%)', WebkitBackdropFilter: 'blur(28px) saturate(170%)', boxShadow: 'var(--glass-sh)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>Schnellzugriff</span>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
              <QuickAction icon={Buildings} farbe="--dw-blau" label="Objekte" onClick={() => onNavigate?.('/admin/objekte')} />
              <QuickAction icon={UsersFill} farbe="--dw-pink" label="Mieter" onClick={() => onNavigate?.('/admin/mieter')} />
              <QuickAction icon={ReceiptFill} farbe="--dw-orange" label="Rechnungen" onClick={() => onNavigate?.('/admin/rechnungen')} />
              <QuickAction icon={Ticket} farbe="--dw-gruen" label="Tickets" onClick={() => onNavigate?.('/admin/tickets')} />
            </div>
          </section>
        </div>

        {/* E-Mail-Assistent (Briefing-Mails nach Priorität, mit Antwort-Entwurf) */}
        {briefingGeladen && (
          <div className="flex flex-col [&>*]:flex-1" style={{ gridColumn: span(sp.mail) }}>
            <EmailAssistentWidget mails={briefingMails} onOpenEmail={openEmail} scope={scope} nutzer={nutzer} />
          </div>
        )}

        {/* Diese Woche über Termine — firmenweiter Kalender, für Mitarbeitende ausgeblendet */}
        {!nurEigene && (
          <div className="flex flex-col" style={{ gridColumn: span(sp.woche), gap: 12 }}>
            <TermineWocheWidget onNavigate={onNavigate} />
            {/* Nächste Termine — firmenweiter Kalender, für Mitarbeitende ausgeblendet */}
            <DashboardWidget farbe="--dw-termin" icon={CalendarCheck} ariaLabel="Termine" radius={28} style={{ flex: 1, padding: '14px 16px' }}>
              <WidgetKopf farbe="--dw-lila" icon={CalendarBlank} titel="Termine" untertitel="Nächste 7 Tage" />
              {naechsteTermine.length > 0 ? (
                <div className="flex flex-col" style={{ gap: 2 }}>
                  {naechsteTermine.map((t) => (
                    <div key={t.id} className="flex items-center" style={{ gap: 10, padding: '7px 8px', borderRadius: 12 }}>
                      <span className="shrink-0" style={{ width: 4, height: 30, borderRadius: 2, background: 'var(--acc)' }} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{t.titel}</span>
                        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                          {new Date(t.start_datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })} · {t.typ ? terminTypLabel(t) : 'Termin'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <WidgetLeer icon={CalendarBlank} text="Keine anstehenden Termine" />
              )}
            </DashboardWidget>
          </div>
        )}

        {/* Charts — für Mitarbeitende auf ihre Zuständigkeit gerechnet */}
        <div className="flex flex-col [&>*]:flex-1" style={{ gridColumn: span(sp.tickets) }}>
          <TicketStatusChart onNavigate={onNavigate} nurEigene={nurEigene} nutzer={nutzer} />
        </div>
        <div className="flex flex-col [&>*]:flex-1" style={{ gridColumn: span(sp.rechnungen) }}>
          <RechnungenChart onNavigate={onNavigate} userProfile={userProfile} nurEigene={nurEigene} nutzer={nutzer} />
        </div>

        {/* Offene Rechnungen */}
        <DashboardWidget farbe="--dw-pink" icon={ReceiptFill} ariaLabel="Offene Rechnungen" spalte={span(sp.offen)} stapel>
          <WidgetKopf farbe="--dw-orange" icon={ReceiptFill} titel="Offene Rechnungen" untertitel="Dringendste zuerst"
            rechts={
              <button type="button" onClick={() => onNavigate?.('/admin/rechnungen')} data-tip="Alle anzeigen – Zur Rechnungsliste"
                className="dw-gruppe flex shrink-0 items-center border-0"
                style={{ height: 32, padding: '0 12px', borderRadius: 16, gap: 5, fontSize: 12.5, fontWeight: 800, color: 'var(--acc)', fontFamily: 'inherit', cursor: 'pointer' }}>
                Alle anzeigen <ArrowRight weight="bold" size={12} />
              </button>
            } />
          {offeneRechnungen.length > 0 ? (
            <div className="flex flex-col" style={{ gap: 2 }}>
              {offeneRechnungen.map((r) => {
                const ueber = isOverdue(r.faelligkeitsdatum);
                return (
                  <button type="button" key={r.id} onClick={() => onNavigate?.('/admin/rechnungen')} data-tip="Rechnungen – Zur Rechnungsliste"
                    className="dw-zeile flex w-full items-center border-0 bg-transparent text-left"
                    style={{ gap: 12, padding: '6px 8px', borderRadius: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                    <span className="flex shrink-0 items-center justify-center"
                      style={{ width: 36, height: 36, borderRadius: 11, background: ueber ? 'var(--bad-soft)' : 'var(--warn-soft)', color: ueber ? 'var(--bad)' : 'var(--warn)' }}>
                      <ReceiptFill weight="fill" size={17} />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{r.unternehmen}</span>
                      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>#{r.rechnungsnummer}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end">
                      <span className="whitespace-nowrap tabular-nums" style={{ fontSize: 13.5, fontWeight: 800 }}>{Number(r.brutto).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                      {r.faelligkeitsdatum && (
                        <span className="whitespace-nowrap" style={{ fontSize: 11.5, fontWeight: 700, color: ueber ? 'var(--bad)' : 'var(--ink3)' }}>
                          {ueber ? 'Überfällig' : `Fällig ${new Date(r.faelligkeitsdatum).toLocaleDateString('de-DE')}`}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <WidgetLeer icon={CheckCircle} text="Keine offenen Rechnungen" farbeIcon="var(--ok)" />
          )}
        </DashboardWidget>

        {/* Letzte Aktivitäten — firmenweiter Verlauf, für Mitarbeitende ausgeblendet */}
        {!nurEigene && (
          <DashboardWidget farbe="--dw-graublau" icon={ClockCounterClockwise} ariaLabel="Letzte Aktivitäten" spalte={span(sp.aktivitaeten)}>
            <WidgetKopf farbe="--dw-gold" icon={ClockCounterClockwise} titel="Letzte Aktivitäten" untertitel="Neueste Einträge im System" />
            {letzteAktivitaeten.length > 0 ? (
              <div className="grid" style={{ gap: 8, gridTemplateColumns: stufe === 'wide' ? 'repeat(3, minmax(0, 1fr))' : stufe === 'mid' ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }}>
                {letzteAktivitaeten.map((a) => {
                  const art = AKTIVITAET[a.typ] ?? AKTIVITAET_SONST;
                  return (
                    <div key={`${a.typ}-${a.id}`} className="flex items-center" style={{ gap: 12, padding: '9px 12px', borderRadius: 16, background: 'var(--fill)' }}>
                      <KopfKachel farbe={art.farbe} icon={art.icon} groesse={36} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>{a.titel}</span>
                        <span className="flex justify-between" style={{ gap: 8, fontSize: 12, color: 'var(--ink3)' }}>
                          <span className="truncate">{a.details}</span>
                          <span className="shrink-0">{formatRelativeTime(a.zeitpunkt)}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-center" style={{ padding: '14px 0', fontSize: 13.5, fontWeight: 700, color: 'var(--ink3)' }}>Keine Aktivitäten vorhanden</span>
            )}
          </DashboardWidget>
        )}
      </div>
      <GlasTooltipSchicht />
    </div>
  );
};


const QuickAction = ({ icon, farbe, label, onClick }: { icon: Icon; farbe: WidgetFarbe; label: string; onClick?: () => void }) => (
  <button type="button" onClick={onClick} data-tip={`${label} – Bereich öffnen`}
    className="dw-quick flex min-w-0 flex-col items-center border-0 bg-transparent p-0"
    style={{ gap: 6, fontFamily: 'inherit', cursor: 'pointer' }}>
    <KopfKachel farbe={farbe} icon={icon} groesse={38} />
    <span className="whitespace-nowrap" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '-0.01em' }}>{label}</span>
  </button>
);

export default DashboardView;
